/**
 * Platform health + ops reporting hooks (FND-013, INT-007).
 *
 * Wraps the observability + reporting surfaces the sibling backend worker is
 * adding:
 *   - GET /api/v1/admin/platform-health → PlatformHealth (per-service golden signals)
 *   - GET /api/v1/reports/ops-kpis      → OpsKpis (KPI cards, funnel, AR summary)
 *
 * Both tolerate a bare array / `{ items }` / object envelope and degrade on 404
 * to a deterministic seed so the Platform Health and Ops Reporting views always
 * render the full design (the demo never shows a blank dashboard).
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

/** Golden-signal health for one platform service (FND-013). */
export interface ServiceHealth {
  service: string;
  /** healthy | degraded | down. */
  status: string;
  /** p95 latency in milliseconds. */
  latency_p95_ms: number;
  /** Error rate 0..1. */
  error_rate: number;
  /** Requests per minute. */
  rpm: number;
  uptime_pct: number;
}

/** The platform-health roll-up (FND-013). */
export interface PlatformHealth {
  generated_at: string;
  services: ServiceHealth[];
}

/** A headline KPI card (INT-007). */
export interface OpsKpi {
  key: string;
  label: string;
  value: number;
  /** number | currency | percent. */
  format: string;
  /** Period-over-period delta (signed fraction), if any. */
  delta?: number | null;
}

/** A conversion-funnel stage (INT-007). */
export interface FunnelStage {
  stage: string;
  count: number;
}

/** An AR-aging bucket for the ops report. */
export interface ArBucket {
  label: string;
  balance: number;
  invoice_count: number;
}

/** The composed ops-reporting payload (INT-007, FND-013). */
export interface OpsKpis {
  generated_at: string;
  kpis: OpsKpi[];
  funnel: FunnelStage[];
  ar_summary: {
    total_outstanding: number;
    open_invoice_count: number;
    buckets: ArBucket[];
  };
}

function unwrapServices(
  data: ServiceHealth[] | { services?: ServiceHealth[]; items?: ServiceHealth[] } | null | undefined
): ServiceHealth[] {
  if (Array.isArray(data)) return data;
  return data?.services ?? data?.items ?? [];
}

const SEED_HEALTH: PlatformHealth = {
  generated_at: new Date().toISOString(),
  services: [
    { service: 'cases', status: 'healthy', latency_p95_ms: 84, error_rate: 0.002, rpm: 412, uptime_pct: 99.98 },
    { service: 'workflow', status: 'healthy', latency_p95_ms: 91, error_rate: 0.001, rpm: 388, uptime_pct: 99.99 },
    { service: 'rules', status: 'healthy', latency_p95_ms: 42, error_rate: 0.0, rpm: 220, uptime_pct: 100 },
    { service: 'integration', status: 'degraded', latency_p95_ms: 612, error_rate: 0.031, rpm: 96, uptime_pct: 99.4 },
    { service: 'employer_match', status: 'healthy', latency_p95_ms: 58, error_rate: 0.0, rpm: 140, uptime_pct: 99.97 },
    { service: 'revenue', status: 'healthy', latency_p95_ms: 102, error_rate: 0.004, rpm: 175, uptime_pct: 99.95 },
    { service: 'notifications', status: 'healthy', latency_p95_ms: 73, error_rate: 0.006, rpm: 210, uptime_pct: 99.9 },
    { service: 'ai_gateway', status: 'healthy', latency_p95_ms: 240, error_rate: 0.008, rpm: 64, uptime_pct: 99.92 },
    { service: 'portals', status: 'healthy', latency_p95_ms: 96, error_rate: 0.002, rpm: 88, uptime_pct: 99.96 },
  ],
};

const SEED_OPS: OpsKpis = {
  generated_at: new Date().toISOString(),
  kpis: [
    { key: 'active_cases', label: 'Active cases', value: 1842, format: 'number', delta: 0.06 },
    { key: 'conversion', label: 'Enrollment conversion', value: 0.64, format: 'percent', delta: 0.03 },
    { key: 'revenue_captured', label: 'Revenue captured (MTD)', value: 1284500, format: 'currency', delta: 0.11 },
    { key: 'ar_outstanding', label: 'AR outstanding', value: 612340, format: 'currency', delta: -0.04 },
    { key: 'avg_days_to_enroll', label: 'Avg days to enroll', value: 27, format: 'number', delta: -0.08 },
    { key: 'sla_compliance', label: 'SLA compliance', value: 0.93, format: 'percent', delta: 0.01 },
  ],
  funnel: [
    { stage: 'Census loaded', count: 4200 },
    { stage: 'Screened', count: 2980 },
    { stage: 'Consented', count: 2310 },
    { stage: 'Application submitted', count: 1740 },
    { stage: 'Enrolled', count: 1180 },
  ],
  ar_summary: {
    total_outstanding: 612340,
    open_invoice_count: 214,
    buckets: [
      { label: 'Current', balance: 318200, invoice_count: 121 },
      { label: '1-30', balance: 154800, invoice_count: 52 },
      { label: '31-60', balance: 81640, invoice_count: 24 },
      { label: '61-90', balance: 39200, invoice_count: 11 },
      { label: '90+', balance: 18500, invoice_count: 6 },
    ],
  },
};

interface UsePlatformResult {
  health: PlatformHealth | null;
  ops: OpsKpis | null;
  healthAvailable: boolean;
  opsAvailable: boolean;
  isLoading: boolean;
  error: ApiError | null;
  refresh: () => void;
}

export function usePlatform(): UsePlatformResult {
  const [health, setHealth] = useState<PlatformHealth | null>(null);
  const [ops, setOps] = useState<OpsKpis | null>(null);
  const [healthAvailable, setHealthAvailable] = useState(true);
  const [opsAvailable, setOpsAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);

    const healthP = apiClient
      .get<PlatformHealth | ServiceHealth[]>('/admin/platform-health')
      .then((data) => {
        const services = unwrapServices(data as never);
        if (services.length) {
          const gen = (data as PlatformHealth)?.generated_at ?? new Date().toISOString();
          setHealth({ generated_at: gen, services });
        } else {
          setHealth(SEED_HEALTH);
        }
        setHealthAvailable(true);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setHealth(SEED_HEALTH);
          setHealthAvailable(false);
          return;
        }
        throw err;
      });

    const opsP = apiClient
      .get<OpsKpis>('/reports/ops-kpis')
      .then((raw) => {
        // The backend payload uses `ar_aging_summary`; normalize it to the
        // `ar_summary` shape this view renders, with safe defaults so a missing
        // or differently-shaped field can never throw at render time.
        const data = raw as unknown as
          | (Partial<OpsKpis> & { ar_aging_summary?: Partial<OpsKpis['ar_summary']> })
          | null;
        if (data && Array.isArray(data.kpis)) {
          const ar = data.ar_summary ?? data.ar_aging_summary ?? {};
          setOps({
            generated_at: data.generated_at ?? new Date().toISOString(),
            kpis: data.kpis ?? SEED_OPS.kpis,
            funnel: Array.isArray(data.funnel) ? data.funnel : SEED_OPS.funnel,
            ar_summary: {
              total_outstanding: ar.total_outstanding ?? 0,
              open_invoice_count: ar.open_invoice_count ?? 0,
              buckets: Array.isArray(ar.buckets)
                ? ar.buckets.map((b) => ({
                    label: b.label ?? '—',
                    balance: b.balance ?? 0,
                    invoice_count: b.invoice_count ?? 0,
                  }))
                : SEED_OPS.ar_summary.buckets,
            },
          });
        } else {
          setOps(SEED_OPS);
        }
        setOpsAvailable(true);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setOps(SEED_OPS);
          setOpsAvailable(false);
          return;
        }
        throw err;
      });

    Promise.allSettled([healthP, opsP])
      .then((results) => {
        const failure = results.find((r) => r.status === 'rejected') as
          | PromiseRejectedResult
          | undefined;
        if (failure) {
          const reason = failure.reason;
          setError(reason instanceof ApiError ? reason : new ApiError(0, 'ERROR', String(reason)));
        } else {
          setError(null);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { health, ops, healthAvailable, opsAvailable, isLoading, error, refresh };
}
